<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'

import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const map2 = computed(() => store.getters['geo/getMap2'])
const mapReady = computed(() => store.getters['geo/getMapReady'])
const images = import.meta.glob('@/assets/img/mapStylesPreview/*.png', { eager: true })

const toggle3DTerrain = () => {
  if (!map.value.getSource('mapbox-dem')) {
    map.value.addSource('mapbox-dem', {
      'type': 'raster-dem',
      'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
      'tileSize': 512,
      'maxzoom': 14
    })
    map.value.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1.5 })
  } else {
    map.value.setTerrain(null)
    map.value.removeSource('mapbox-dem')
  }
}

const toggle3DBuildings = () => {
  const buildingLayerId = 'add-3d-buildings'

  if (map.value.getLayer(buildingLayerId)) {
    map.value.removeLayer(buildingLayerId)
    return
  }

  const layers = map.value.getStyle().layers
  const labelLayer = layers.find(
    (layer) => layer.type === 'symbol' && layer.layout?.['text-field']
  )

  if (!labelLayer) {
    console.warn('No symbol layer found for positioning 3D buildings')
    return
  }

  const labelLayerId = labelLayer.id

  if (!map.value.getSource('composite')) {
    console.warn('Composite source not found, skipping 3D buildings layer')
    return
  }

  map.value.addLayer(
    {
      id: buildingLayerId,
      source: 'composite',
      'source-layer': 'building',
      filter: ['==', 'extrude', 'true'],
      type: 'fill-extrusion',
      minzoom: 15,
      paint: {
        'fill-extrusion-color': '#aaa',
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15,
          0,
          15.05,
          ['get', 'height']
        ],
        'fill-extrusion-base': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15,
          0,
          15.05,
          ['get', 'min_height']
        ],
        'fill-extrusion-opacity': 0.6
      }
    },
    // Add the 3D buildings layer below the first symbol/text layer
    labelLayerId
  )
}

const isLabels = ref(true)
const isBuildings = ref(false)
const isTerrain = ref(false)

const styles = ref([
  {
    selected: true,
    label: 'Outdoors',
    value: 'mapbox://styles/mapbox/outdoors-v12',
    preview: images['/src/assets/img/mapStylesPreview/outdoor.png'].default,
    theme: 'dark'
  },
  {
    selected: false,
    label: 'Satellite',
    value: 'mapbox://styles/mapbox/standard-satellite',
    preview: images['/src/assets/img/mapStylesPreview/satellite.png'].default,
    theme: 'light'
  },
  {
    selected: false,
    label: 'Light',
    value: 'mapbox://styles/mapbox/light-v11',
    preview: images['/src/assets/img/mapStylesPreview/light.png'].default,
    theme: 'dark'
  },
  {
    selected: false,
    label: 'Dark',
    value: 'mapbox://styles/mapbox/dark-v11',
    preview: images['/src/assets/img/mapStylesPreview/dark.png'].default,
    theme: 'light'
  }
])

const selectedStyle = computed(() => styles.value.find((style) => style.selected))

const switchMapStyle = (newStyle) => {
  styles.value.forEach((styleObj) => {
    styleObj.selected = styleObj.value === newStyle
  })

  map.value.setStyle(newStyle)

  if (map2.value) {
    map2.value.setStyle(newStyle)
  }

  isLabels.value = true
  isBuildings.value = false
  isTerrain.value = false
}

const switchLabels = () => {
  isLabels.value = !isLabels.value
  if (selectedStyle.value.label === 'Satellite') {
    map.value.setConfig('basemap', {
      'showPlaceLabels': isLabels.value,
      'showRoadsAndTransit': isLabels.value,
      'showRoadLabels': isLabels.value,
      'showPointOfInterestLabels': isLabels.value,
      'showTransitLabels': isLabels.value,
    })
  } else {
    map.value.style.stylesheet.layers.forEach((layer) => {
      if (layer.type === 'symbol' || layer.type === 'line') {
        map.value.setLayoutProperty(layer.id, 'visibility', isLabels.value ? 'visible' : 'none')
      }
    })
  }
}

const switchBuildings = () => {
  isBuildings.value = !isBuildings.value
  toggle3DBuildings()
}

const switchTerrain = () => {
  isTerrain.value = !isTerrain.value
  toggle3DTerrain()
}
</script>

<template>
  <Popover v-if="map && mapReady">
    <PopoverTrigger>
      <img
        :src="selectedStyle.preview"
        class="w-10 h-10 object-cover rounded border-2 cursor-pointer shadow-lg"
        :class="{
          'border-white': selectedStyle.theme === 'light',
          'border-gray-700': selectedStyle.theme === 'dark'
        }"
      />
    </PopoverTrigger>
    <PopoverContent class="p-0 w-full text-sm">
      <div class="p-2 space-y-2">
        <div class="space-y-1">
          <div
            v-for="(style, index) in styles"
            :key="index"
          >
            <label
              :for="`basemap-${style.value}`"
              class="inline-flex items-center cursor-pointer space-x-2"
            >
              <img
                :src="style.preview"
                class="w-10 h-10 object-cover rounded border-2 border-gray-300"
                :class="{ 'border-primary': style.selected }"
              />
              <input
                type="radio"
                :id="`basemap-${style.value}`"
                name="basemap"
                :value="style.value"
                @change="switchMapStyle(style.value)"
                class="hidden"
              />
              <span
                :class="{ 'text-primary': style.selected }"
              >{{ style.label }}</span>
            </label>
          </div>
        </div>
        <div class="flex items-center">
          <Switch
            :model-value="isLabels"
            @update:model-value="switchLabels"
          />
          <Label class="ml-2">Labels</Label>
        </div>
        <div class="flex items-center">
          <Switch
            :model-value="isBuildings"
            @update:model-value="switchBuildings"
          />
          <Label class="ml-2">3D Buildings</Label>
        </div>
        <div class="flex items-center">
          <Switch
            :model-value="isTerrain"
            @update:model-value="switchTerrain"
          />
          <Label class="ml-2">3D Terrain</Label>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
